import { ChangeEvent, FC, FormEvent, useEffect, useState } from "react";
import Breadcrumb, { Crumb } from "../../../components/common/Breadcrumbs";
import { useNavigate, useParams } from "react-router-dom";
import { useGetAllTenantsQuery } from "../tenant/tenantApi";
import {
    useCreateSystemPropertyMutation,
    useGetSystemPropertyByIdQuery,
    useUpdateSystemPropertyByIdMutation,
} from "./systemPropertiesApi";
import { handleRTKQuery } from "../../../helpers/utils/RtkQueryUtils";
import { Routes as AppRoutes } from "../../../routes";
import AuditableInfoButton from "../../../components/common/form/AuditableInfoButton";
import Form from "../../../components/common/form/Form";
import InputField from "../../../components/common/form/InputField";
import SelectField from "../../../components/common/form/SelectField";
import { createOptionListForSelectTag } from "../../../helpers/utils/Utils";
import ButtonTray from "../../../components/common/form/ButtonTray";
import { hasPermission } from "../../../helpers/utils/AccessControlUtils";

interface Props {
    crumbs: Crumb[];
    title: string;
    btnText: string;
    isReadOnly?: boolean;
}

const SystemPropertyField: FC<Props> = ({ crumbs, title, btnText, isReadOnly }) => {
    let { systemPropertyId } = useParams();
    const { data: tenants } = useGetAllTenantsQuery("");
    const navigate = useNavigate();
    const { data: systemPropertyData, isLoading: systemPropertyLoading } =
        useGetSystemPropertyByIdQuery(systemPropertyId);
    const [
        updateSystemProperty,
        updateSystemPropertyApiResponse,
    ] = useUpdateSystemPropertyByIdMutation();
    const [
        createSystemProperty,
        createSystemPropertyApiResponse,
    ] = useCreateSystemPropertyMutation();
    const [systemProperty, setSystemProperty] = useState<any>({});

    useEffect(() => {
        if (systemPropertyData) {
            setSystemProperty({
                ...systemPropertyData,
                tenant: systemPropertyData.tenant
                    ? { value: systemPropertyData.tenant, label: systemPropertyData.tenant }
                    : null,
            });
        }
    }, [systemPropertyData]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const updatedData = {
            ...systemProperty,
            tenant: systemProperty.tenant?.value,
        };

        const createOrUpdateSystemProperty = async () => {

            if (btnText === "Create") {
                return await createSystemProperty(updatedData).unwrap();
            } else {
                return await updateSystemProperty({
                    id: systemPropertyId,
                    requestBody: updatedData,
                }).unwrap();
            }
        };

        await handleRTKQuery(createOrUpdateSystemProperty, (response: any) => {
            if (response?.success || response?.data?.success) {
                setSystemProperty({});
                navigate(AppRoutes.SystemProperties.path);
            }
        });
    };

    const handleOnChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { name: string; value: any }
    ) => {
        const { name, value } = "target" in e ? e.target : e;
        setSystemProperty((prevSystemProperty: any) => ({
            ...prevSystemProperty,
            [name]: value,
        }));
    };

    return (
        <div className="adduser_container px-4 py-2">
            <div className="justify-between sm:block md:flex lg:flex">
                <Breadcrumb crumbs={crumbs} />
                {btnText !== "Create" &&  hasPermission( ["AUDIT-V"]) && <AuditableInfoButton obj={systemProperty}/>
                }
            </div>

            <div className="flex justify-center mt-3">
                <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                    <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-7">
                        {title}
                    </h3>
                    <Form
                        onSubmit={handleSubmit}
                        fields={
                            <>
                                <InputField
                                    name="name"
                                    label="Name"
                                    onChange={handleOnChange}
                                    value={systemProperty?.name || ""}
                                    required
                                    placeholder="Enter Name..."
                                    disabled={isReadOnly}
                                />
                                <InputField
                                    name="value"
                                    label="Value"
                                    onChange={handleOnChange}
                                    value={systemProperty?.value || ""}
                                    required
                                    placeholder="Enter Value..."
                                    disabled={isReadOnly}
                                />
                                <SelectField
                                    label="Tenant"
                                    value={systemProperty?.tenant}
                                    options={createOptionListForSelectTag(tenants, "name", "name")}
                                    onChange={(option) =>
                                        handleOnChange({ name: "tenant", value: option })
                                    }
                                    isDisabled={isReadOnly}
                                />

                                {!isReadOnly &&
                                    <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                        <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                        </div>
                                        <div className="relative col-span-9">
                                            <div className={"mt-4 flex"}>
                                                <ButtonTray
                                                    buttons={[
                                                        {
                                                            buttonProps: {
                                                                btnText: "Cancel",
                                                                onClick: () => navigate(AppRoutes.SystemProperties.path),
                                                                type: "cancel",
                                                            },
                                                            buttonType: "button",
                                                        },
                                                        {
                                                            buttonProps: {
                                                                btnText: "Submit",
                                                                type: "submit",
                                                                isLoading:
                                                                    createSystemPropertyApiResponse.isLoading ||
                                                                    updateSystemPropertyApiResponse.isLoading,
                                                                isVisible: hasPermission(["SPRO-U"]),
                                                            },
                                                            buttonType: "button",
                                                        },
                                                    ]}
                                                    align="center"
                                                    gap={2}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                }
                            </>
                        }
                    />
                </div>
            </div>
        </div>
    );
};

export default SystemPropertyField;
